import React, { Suspense, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AppThemeProvider, useAppTheme } from '../contexts/ThemeContext';
import { createMuiAppTheme } from '../theme/muiTheme.js';
import { BriefProvider } from '../contexts/BriefContext.js';
import { AuthProvider, useAuth } from '../contexts/AuthContext.js';
import { loginProviders } from '../auth/loginProviders.js';
import App from '../App.js';
import AuthGuard from '../components/AuthGuard.js';
import ErrorBoundary from '../components/layout/ErrorBoundary.js';
import NotFound from '../components/layout/NotFound.js';

const ChatView = React.lazy(() => import('../views/chat/ChatView.js'));
const AgentsView = React.lazy(() => import('../views/agents/AgentsView.js'));
const AgentsListView = React.lazy(() => import('../views/agents/AgentsListView.js'));
const SettingsView = React.lazy(() => import('../views/settings/SettingsView'));
const MyAccountView = React.lazy(() => import('../views/settings/MyAccountView'));
const AccountProfileView = React.lazy(() => import('../views/settings/AccountProfileView'));
const MCPToolsView = React.lazy(() => import('../views/agents/MCPToolsView.js'));
const WorkerStatusView = React.lazy(() => import('../views/development/WorkerStatusView.js'));
const BriefEntryView = React.lazy(() => import('../views/briefs/BriefEntryView.js'));
const BriefsListView = React.lazy(() => import('../views/briefs/BriefsListView'));
const ProductDiscoveryView = React.lazy(() => import('../views/briefs/ProductDiscoveryView.js'));
const MediaBuyCreationView = React.lazy(() => import('../views/media-buys/MediaBuyCreationView.js'));
const MediaBuysListView = React.lazy(() => import('../views/media-buys/MediaBuysListView.js'));
const MediaBuyDetailsView = React.lazy(() => import('../views/media-buys/MediaBuyDetailsView.js'));
const CreativeLibraryView = React.lazy(() => import('../views/creatives/CreativeLibraryView'));
const MediaBuyDeliveryView = React.lazy(() => import('../views/media-buys/MediaBuyDeliveryView.js'));
const ProductsListView = React.lazy(() => import('../views/administration/ProductsListView.js'));
const ProductFormView = React.lazy(() => import('../views/administration/ProductFormView.js'));
const PackagesListView = React.lazy(() => import('../views/administration/PackagesListView.js'));
const PackageFormView = React.lazy(() => import('../views/administration/PackageFormView.js'));
const AgentSessionsListView = React.lazy(() => import('../views/agents/AgentSessionsListView.js'));
const AgentSessionDetailView = React.lazy(() => import('../views/agents/AgentSessionDetailView.js'));
const ComponentsView = React.lazy(() => import('../views/development/ComponentsView.js'));
const VervePlusGridView = React.lazy(() => import('../views/development/VervePlusGridView.js'));
const AgentFlowView = React.lazy(() => import('../views/development/AgentFlowView.js'));
const AgentStudioView = React.lazy(() => import('../views/agents/AgentStudioView.js'));
const AccountsView = React.lazy(() => import('../views/administration/AccountsView'));
const RolesView = React.lazy(() => import('../views/administration/RolesView.js'));
const PoliciesView = React.lazy(() => import('../views/administration/PoliciesView.js'));
const SellersView = React.lazy(() => import('../views/administration/SellersView.js'));
const SellerCreativeApprovalView = React.lazy(() => import('../views/administration/SellerCreativeApprovalView.js'));
const McpServersListView = React.lazy(() => import('../views/agents/McpServersListView.js'));
const AdsManagerView = React.lazy(() => import('../views/ads-manager/AdsManagerView.js'));
const LoginView = React.lazy(() => import('../components/layout/LoginView.js'));

const LoginPage = () => {
  const { login, isLoading, error } = useAuth();
  return <LoginView providers={loginProviders} onLogin={login} isLoading={isLoading} error={error} />;
};

/**
 * Wrapper component for BriefEntryView that forces remount when briefId changes
 * This ensures all state is reset when navigating between different briefs or creating a new brief
 */
const BriefEntryViewWrapper = () => {
  const { briefId } = useParams();
  // Use briefId as key - when it changes, React will unmount and remount the component
  return <BriefEntryView key={briefId || 'new'} />;
};

// Define the router configuration
const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthGuard>
        <App />
      </AuthGuard>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '',
        element: <ChatView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'chat',
        element: <ChatView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'chat/:sessionId',
        element: <ChatView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'settings',
        element: <SettingsView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'settings/my-account',
        element: <MyAccountView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'account',
        element: <AccountProfileView />,
        errorElement: <ErrorBoundary />,
      },
      // Ad Manager routes
      {
        path: 'briefs',
        element: <BriefsListView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'briefs/new',
        element: <BriefEntryViewWrapper />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'briefs/:briefId/edit',
        element: <BriefEntryViewWrapper />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'briefs/:briefId/products',
        element: <ProductDiscoveryView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'media-buys',
        element: <MediaBuysListView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'media-buys/:mediaBuyId',
        element: <MediaBuyDetailsView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'media-buys/:mediaBuyId/delivery',
        element: <MediaBuyDeliveryView />,
        errorElement: <ErrorBoundary />,
      },
      {
        path: 'creatives',
        element: <CreativeLibraryView />,
        errorElement: <ErrorBoundary />,
      },
      // Sellers routes (legacy - kept for backward compatibility)
      {
        path: 'sellers',
        children: [
          {
            path: 'packages',
            element: <PackagesListView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'packages/new',
            element: <PackageFormView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'packages/:packageId/edit',
            element: <PackageFormView />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      // Development routes
      {
        path: 'development',
        children: [
          {
            path: 'components',
            element: <ComponentsView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'verve-plus-grid',
            element: <VervePlusGridView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'agent-flow',
            element: <AgentFlowView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'worker-status',
            element: <WorkerStatusView />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      // Agents routes (static paths before dynamic :agentId)
      {
        path: 'agents',
        children: [
          {
            path: '',
            element: <AgentsListView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'studio',
            element: <AgentStudioView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sessions',
            element: <AgentSessionsListView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sessions/:sessionId',
            element: <AgentSessionDetailView />,
            errorElement: <ErrorBoundary />,
          },
          // MCP routes
          {
            path: 'mcp-servers',
            element: <McpServersListView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'mcp-servers/tools',
            element: <MCPToolsView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: ':agentId',
            element: <AgentsView />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      // Administration routes
      {
        path: 'administration',
        children: [
          {
            path: 'accounts',
            element: <AccountsView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'accounts/:accountId',
            element: <AccountsView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'roles',
            element: <RolesView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'roles/:roleId',
            element: <RolesView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'policies',
            element: <PoliciesView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'policies/:policyId',
            element: <PoliciesView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers',
            element: <SellersView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/:sellerId',
            element: <SellersView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/:sellerId/creatives',
            element: <SellerCreativeApprovalView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/products',
            element: <ProductsListView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/products/new',
            element: <ProductFormView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/products/:productId/edit',
            element: <ProductFormView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/packages',
            element: <PackagesListView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/packages/new',
            element: <PackageFormView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'sellers/packages/:packageId/edit',
            element: <PackageFormView />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      // Ads Manager routes (new interface)
      {
        path: 'ads-manager',
        children: [
          {
            path: '',
            element: <AdsManagerView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'discovery',
            element: <AdsManagerView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'deals',
            element: <AdsManagerView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'campaigns',
            element: <AdsManagerView />,
            errorElement: <ErrorBoundary />,
          },
          {
            path: 'creatives',
            element: <AdsManagerView />,
            errorElement: <ErrorBoundary />,
          },
        ],
      },
      // Catch-all route for 404s
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  // Login route outside AuthGuard to prevent redirect loop
  {
    path: '/login',
    element: <LoginPage />,
  },
  // Root level catch-all for any path outside /
  {
    path: '*',
    element: <NotFound />,
  },
]);

/**
 * Inner shell that reads the current app theme from context
 * and provides the corresponding MUI theme.
 */
const MuiThemeShell = ({ children }) => {
  const { theme: appTheme } = useAppTheme();
  const muiTheme = useMemo(() => createMuiAppTheme(appTheme), [appTheme]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

const AppRouter = () => {
  return (
    <AuthProvider>
      <AppThemeProvider>
        <MuiThemeShell>
          <BriefProvider>
            <Suspense fallback={<div style={{ padding: '24px' }}>Loading...</div>}>
              <RouterProvider router={router} />
            </Suspense>
          </BriefProvider>
        </MuiThemeShell>
      </AppThemeProvider>
    </AuthProvider>
  );
};

export default AppRouter;
